import React, { useState, useMemo, useEffect } from 'react';
import { useMsal, useAccount } from "@azure/msal-react";
import { MaterialReactTable } from 'material-react-table';
import { MenuItem } from "@mui/material";
import Modal from 'react-modal';
import * as Utils from '../lib/utils';

const bulkModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    height: '80vh',
    width: '700px',
    overflow: 'auto',
  },
};

const JobTitleAlias = () => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0] || {});

  const [tableData, setTableData] = useState([]);
  const [employeeTitles, setEmployeeTitles] = useState([]);
  const [salaryRangeTitles, setSalaryRangeTitles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [newJobTitle, setNewJobTitle] = useState("");
  const [newSalaryRangeTitle, setNewSalaryRangeTitle] = useState("");

  const [bulkModalIsOpen, setBulkModalIsOpen] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState({});
  const [isBulkSaving, setIsBulkSaving] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [aliases, empTitles, rangeTitles] = await Promise.all([
        Utils.getData('/api/job_title_alias', instance, account),
        Utils.getData('/api/job_title_alias/employee_titles', instance, account),
        Utils.getData('/api/job_title_alias/salary_range_titles', instance, account),
      ]);

      setTableData(aliases || []);
      setEmployeeTitles(empTitles || []);
      setSalaryRangeTitles(rangeTitles || []);
    } catch (error) {
      setIsError(true);
      console.error(error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveAlias = (jobTitle, salaryRangeTitle) => {
    return Utils.postData(
      "/api/job_title_alias",
      { job_title: jobTitle, salary_range_title: salaryRangeTitle },
      instance,
      account
    );
  };

  const deleteAlias = (id) => {
    if (window.confirm("Are you sure you want to delete this alias?")) {
      Utils.deleteData(
        "/api/job_title_alias/" + id,
        instance,
        account
      ).then(() => {
        fetchData();
      }).catch((error) => {
        console.error("Error deleting alias:", error);
        alert("There was an error deleting the alias.");
      });
    }
  };

  const handleAddAlias = () => {
    if (!newJobTitle) {
      alert("Please select a job title.");
      return;
    }
    if (!newSalaryRangeTitle) {
      alert("Please select a salary range title.");
      return;
    }
    saveAlias(newJobTitle, newSalaryRangeTitle).then(() => {
      fetchData();
      setNewJobTitle("");
      setNewSalaryRangeTitle("");
    }).catch((error) => {
      console.error("Error saving alias:", error);
      alert("There was an error saving the alias.");
    });
  };

  const openBulkModal = () => {
    // Initialize bulk assignments as empty for each unassigned title
    const initial = {};
    employeeTitles.forEach((title) => {
      initial[title] = "";
    });
    setBulkAssignments(initial);
    setBulkModalIsOpen(true);
  };

  const closeBulkModal = () => {
    setBulkModalIsOpen(false);
  };

  const handleBulkAssignmentChange = (jobTitle, salaryRangeTitle) => {
    setBulkAssignments((prev) => ({
      ...prev,
      [jobTitle]: salaryRangeTitle,
    }));
  };

  const handleSuggest = () => {
    const newAssignments = { ...bulkAssignments };

    const engineerRangeTitles = salaryRangeTitles.filter(t => t.toLowerCase().includes("engineer"));
    const scientistRangeTitles = salaryRangeTitles.filter(t => t.toLowerCase().includes("scientist"));

    employeeTitles.forEach((jobTitle) => {
      // Skip if already assigned
      if (newAssignments[jobTitle]) return;

      const suggestion = Utils.suggestTitleAlias(jobTitle, engineerRangeTitles, scientistRangeTitles);
      if (suggestion) {
        newAssignments[jobTitle] = suggestion;
      }
    });

    setBulkAssignments(newAssignments);
  };

  const handleBulkSave = async () => {
    const assignments = Object.entries(bulkAssignments).filter(
      ([jobTitle, rangeTitle]) => rangeTitle && rangeTitle !== ""
    );

    if (assignments.length === 0) {
      alert("No assignments have been selected.");
      return;
    }

    setIsBulkSaving(true);
    try {
      for (let i = 0; i < assignments.length; i++) {
        await saveAlias(assignments[i][0], assignments[i][1]);
      }
      alert(assignments.length + " alias(es) saved successfully.");
      closeBulkModal();
      fetchData();
    } catch (error) {
      console.error("Error during bulk save:", error);
      alert("An error occurred during bulk save. Some aliases may have been saved.");
      fetchData();
    }
    setIsBulkSaving(false);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'job_title',
        header: 'Employee Job Title',
        enableEditing: false,
      },
      {
        accessorKey: 'salary_range_title',
        header: 'Salary Range Title (Alias)',
        enableEditing: true,
        muiEditTextFieldProps: ({ row }) => ({
          select: true,
          children: salaryRangeTitles.map((title) => (
            <MenuItem key={title} value={title}>
              {title}
            </MenuItem>
          )),
          onChange: (event) => {
            saveAlias(row.original.job_title, event.target.value).then(() => {
              fetchData();
            });
          },
        }),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableEditing: false,
        Cell: ({ row }) => (
          <button
            className="btn btn-sm btn-danger"
            onClick={() => deleteAlias(row.original.id)}
          >
            Delete
          </button>
        ),
      },
    ],
    [salaryRangeTitles],
  );

  const handleSaveRow = async ({ exitEditingMode, row, values }) => {
    exitEditingMode();
  };

  const tableHeightValue = window.innerHeight > 300 ? window.innerHeight - 300 : 400;
  const tableHeightCSS = tableHeightValue + "px";

  return (
    <div>
      <Modal
        isOpen={bulkModalIsOpen}
        onRequestClose={closeBulkModal}
        style={bulkModalStyles}
        contentLabel="Bulk Assign Aliases"
        ariaHideApp={false}
      >
        <h2 className="modalTitle">Bulk Assign Job Title Aliases</h2>
        <div className="modalBody">
          <p>Select a salary range title for each employee job title below. Leave blank to skip.</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #ccc" }}>
                <th style={{ textAlign: "left", padding: "6px" }}>Employee Job Title</th>
                <th style={{ textAlign: "left", padding: "6px" }}>Salary Range Title</th>
              </tr>
            </thead>
            <tbody>
              {employeeTitles.map((title) => (
                <tr key={title} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "6px", fontSize: "13px" }}>{title}</td>
                  <td style={{ padding: "6px" }}>
                    <select
                      value={bulkAssignments[title] || ""}
                      onChange={(e) => handleBulkAssignmentChange(title, e.target.value)}
                      style={{ padding: "3px", width: "100%", fontSize: "13px" }}
                    >
                      <option value="">-- Select --</option>
                      {salaryRangeTitles.map((rangeTitle) => (
                        <option key={rangeTitle} value={rangeTitle}>{rangeTitle}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {employeeTitles.length === 0 && (
            <p style={{ marginTop: "10px", color: "#666" }}>All employee job titles already have aliases assigned.</p>
          )}
        </div>
        <div className="modalFooter" style={{ marginTop: "15px", paddingTop: "10px", borderTop: "1px solid #ddd" }}>
          <button
            className="btn btn-primary"
            onClick={handleSuggest}
            disabled={isBulkSaving || employeeTitles.length === 0}
          >
            Suggest
          </button>
          &nbsp;&nbsp;
          <button
            className="btn btn-primary"
            onClick={handleBulkSave}
            disabled={isBulkSaving}
          >
            {isBulkSaving ? "Saving..." : "Save All"}
          </button>
          &nbsp;&nbsp;
          <button className="btn btn-primary" onClick={closeBulkModal}>
            Cancel
          </button>
        </div>
      </Modal>

      <div style={{ padding: "10px", borderBottom: "1px solid #ddd", marginBottom: "10px" }}>
        <strong>Add New Alias:</strong>&nbsp;&nbsp;
        <select
          value={newJobTitle}
          onChange={(e) => setNewJobTitle(e.target.value)}
          style={{ padding: "4px", minWidth: "250px" }}
        >
          <option value="">-- Select Employee Job Title --</option>
          {employeeTitles.map((title) => (
            <option key={title} value={title}>{title}</option>
          ))}
        </select>
        &nbsp;&nbsp;
        <span>→</span>
        &nbsp;&nbsp;
        <select
          value={newSalaryRangeTitle}
          onChange={(e) => setNewSalaryRangeTitle(e.target.value)}
          style={{ padding: "4px", minWidth: "250px" }}
        >
          <option value="">-- Select Salary Range Title --</option>
          {salaryRangeTitles.map((title) => (
            <option key={title} value={title}>{title}</option>
          ))}
        </select>
        &nbsp;&nbsp;
        <button className="btn btn-primary" onClick={handleAddAlias}>
          Add Alias
        </button>
        &nbsp;&nbsp;
        <button className="btn btn-primary" onClick={openBulkModal} disabled={employeeTitles.length === 0}>
          Bulk Assign
        </button>
      </div>

      {!bulkModalIsOpen && <MaterialReactTable
        renderTopToolbarCustomActions={() => (
          <div>
            <span className="sectionTableHeader">Job Title Aliases</span>
            &nbsp;&nbsp;&nbsp;
            <a href="/salaryGroups">← Back to Salary Groups</a>
          </div>
        )}
        columns={columns}
        data={tableData}
        globalFilterFn="contains"
        enableStickyHeader
        enableEditing
        editDisplayMode="row"
        onEditingRowSave={handleSaveRow}
        muiToolbarAlertBannerProps={
          isError ? { color: 'error', children: 'Error loading data' } : undefined
        }
        muiTableContainerProps={{ sx: { height: tableHeightCSS } }}
        state={{
          isLoading,
          showAlertBanner: isError,
        }}
      />}
    </div>
  );
};

export default JobTitleAlias;
